import { useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import {
    BookOpen,
    CalendarBlank,
    Receipt,
    AirplaneTilt,
    ChartBar,
    UsersThree,
    Buildings,
    Briefcase,
    ShieldCheck,
    Database,
    GridFour,
    Car,
    Question,
    Warning,
    Lightbulb,
} from "@phosphor-icons/react";

/**
 * Manual de usuario navegable. Cada sección tiene un anchor id para el
 * TOC lateral. Los apartados admin/PM solo se muestran a esos roles.
 */
export default function Manual() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const isPmOrAdmin = user?.role === "admin" || user?.role === "pm";

    const sections = useMemo(() => {
        const base = [
            { id: "intro", label: "Introducción", icon: BookOpen },
            { id: "timesheet", label: "Parte de horas", icon: CalendarBlank },
            { id: "expenses", label: "Gastos y dietas", icon: Receipt },
            { id: "absences", label: "Vacaciones y ausencias", icon: AirplaneTilt },
            { id: "projects-request", label: "Solicitar proyectos", icon: Briefcase },
            { id: "reports", label: "Informes", icon: ChartBar },
        ];
        if (isPmOrAdmin) {
            base.push({
                id: "pm",
                label: "Rol Project Manager",
                icon: ShieldCheck,
            });
        }
        if (isAdmin) {
            base.push(
                { id: "admin-users", label: "Usuarios", icon: UsersThree },
                { id: "admin-projects", label: "Proyectos y presupuesto", icon: Briefcase },
                { id: "admin-customers", label: "Clientes", icon: Buildings },
                { id: "admin-matrix", label: "Matriz de vacaciones", icon: GridFour },
                { id: "admin-approvals", label: "Aprobaciones", icon: ShieldCheck },
                { id: "admin-invoices", label: "Facturación", icon: Receipt },
                { id: "admin-data", label: "Importar / exportar", icon: Database },
            );
        } else if (isPmOrAdmin) {
            base.push({ id: "admin-invoices", label: "Facturación", icon: Receipt });
        }
        base.push({ id: "faq", label: "Preguntas frecuentes", icon: Question });
        return base;
    }, [isAdmin, isPmOrAdmin]);

    const [active, setActive] = useState("intro");

    const jump = (id) => {
        setActive(id);
        const el = document.getElementById(`manual-${id}`);
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    return (
        <div data-testid="page-manual">
            <PageHeader
                eyebrow="Ayuda"
                title="Manual de usuario"
                description="Guía rápida de todas las funcionalidades de Ingartek Portal."
            />
            <div className="p-6 lg:p-10 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
                {/* TOC */}
                <aside className="lg:sticky lg:top-6 lg:self-start">
                    <div
                        className="border border-slate-200 bg-white"
                        data-testid="manual-toc"
                    >
                        <div className="px-4 py-3 border-b border-slate-100 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                            Índice
                        </div>
                        <nav className="py-2">
                            {sections.map((s) => {
                                const Icon = s.icon;
                                return (
                                    <button
                                        key={s.id}
                                        onClick={() => jump(s.id)}
                                        className={`w-full flex items-center gap-2 text-left px-4 py-2 text-sm transition-colors ${
                                            active === s.id
                                                ? "bg-[#002BAF]/5 text-[#002BAF] font-semibold border-l-2 border-[#002BAF]"
                                                : "text-slate-700 hover:bg-slate-50 border-l-2 border-transparent"
                                        }`}
                                        data-testid={`manual-toc-${s.id}`}
                                    >
                                        <Icon size={14} />
                                        {s.label}
                                    </button>
                                );
                            })}
                        </nav>
                    </div>
                </aside>

                {/* Content */}
                <div className="space-y-10 max-w-3xl">
                    <Section id="intro" title="Bienvenido a Ingartek Portal">
                        <p>
                            Esta aplicación centraliza el <b>registro de horas</b>,{" "}
                            <b>gastos</b>, <b>vacaciones</b> e informes de facturación de
                            Ingartek. El acceso está limitado al dominio{" "}
                            <code>@ingartek.com</code> mediante Google.
                        </p>
                        <Callout icon={Lightbulb} tone="info">
                            Consejo: usa el buscador de la esquina superior en las tablas.
                            La mayoría de columnas se pueden ordenar haciendo clic en su
                            cabecera.
                        </Callout>
                        <SubHeading>Roles</SubHeading>
                        <ul className="list-disc list-inside space-y-1">
                            <li>
                                <b>Empleado</b>: registra horas, gastos, ausencias y solicita
                                asignación a proyectos.
                            </li>
                            <li>
                                <b>Project Manager</b>: además, aprueba solicitudes de
                                asignación a sus proyectos.
                            </li>
                            <li>
                                <b>Administrador</b>: gestión completa (usuarios, proyectos,
                                clientes, presupuestos, matriz de vacaciones, datos).
                            </li>
                        </ul>
                    </Section>

                    <Section id="timesheet" title="Parte de horas">
                        <p>
                            La sección <b>Parte de horas</b> muestra una tabla con los
                            proyectos a los que estás asignado y una columna por cada día
                            de la semana seleccionada. Introduce las horas dedicadas
                            directamente en la celda correspondiente.
                        </p>
                        <SubHeading>Cómo imputar horas</SubHeading>
                        <ol className="list-decimal list-inside space-y-1">
                            <li>Selecciona la semana con los controles superiores.</li>
                            <li>
                                Escribe las horas en la celda del proyecto y día
                                correspondiente (admite decimales, ej. <code>1,5</code>).
                            </li>
                            <li>
                                Añade un comentario opcional pulsando sobre la celda.
                            </li>
                            <li>Los cambios se guardan automáticamente.</li>
                        </ol>
                        <Callout icon={Warning} tone="warning">
                            Los proyectos <b>archivados</b>, cerrados o con fecha
                            <code> DATE_END </code>
                            anterior a hoy no aparecen. Si un proyecto que necesitas ya no
                            está visible, pide al administrador que lo reactive o extienda
                            la asignación.
                        </Callout>
                        <SubHeading>Cómo pedir un nuevo proyecto</SubHeading>
                        <p>
                            Pulsa <b>“Solicitar proyecto”</b> en la parte superior de la
                            tabla, elige los proyectos que necesitas y envía la solicitud.
                            Recibirás la aprobación del Project Manager del proyecto o de
                            un administrador.
                        </p>
                    </Section>

                    <Section id="expenses" title="Gastos y dietas">
                        <p>
                            Registra tus gastos imputados a un proyecto: dietas,
                            desplazamientos, alojamiento, material u otros. Cada gasto
                            genera un asiento contable por proyecto.
                        </p>
                        <SubHeading>Nuevo gasto</SubHeading>
                        <ol className="list-decimal list-inside space-y-1">
                            <li>
                                Pulsa <b>“Nuevo gasto”</b>. Aparece un panel a la derecha.
                            </li>
                            <li>
                                Selecciona el <b>proyecto</b> (puedes escribir para buscar).
                            </li>
                            <li>Elige fecha, categoría e importe.</li>
                            <li>
                                Si la categoría es <b>Desplazamiento</b>, aparecerá el
                                campo <b>Km recorridos</b>{" "}
                                <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                                    <Car size={12} /> (solo si vas en coche)
                                </span>
                                . El importe se introduce a mano.
                            </li>
                            <li>Añade una descripción opcional y guarda.</li>
                        </ol>
                        <SubHeading>Exportar</SubHeading>
                        <p>
                            El botón <b>“Exportar CSV”</b> descarga un fichero compatible
                            con Excel (UTF-8 con BOM, separador <code>;</code>). Respeta
                            los filtros aplicados de fechas y proyecto.
                        </p>
                    </Section>

                    <Section id="absences" title="Vacaciones y ausencias">
                        <p>
                            Solicita vacaciones o cualquier tipo de ausencia desde{" "}
                            <b>Vacaciones y ausencias</b>. Verás tu resumen anual (días
                            asignados, consumidos y pendientes).
                        </p>
                        <SubHeading>Solicitar una ausencia</SubHeading>
                        <ol className="list-decimal list-inside space-y-1">
                            <li>Pulsa “Nueva solicitud”.</li>
                            <li>
                                Selecciona el <b>tipo</b>: vacaciones, personal, baja
                                médica, formación…
                            </li>
                            <li>
                                Indica <b>fecha de inicio y fin</b> (ambos incluidos).
                                Solo se contabilizan los días laborables.
                            </li>
                            <li>Añade un comentario opcional y envía.</li>
                        </ol>
                        <Callout icon={Lightbulb} tone="info">
                            Puedes cancelar una solicitud <b>pendiente</b> desde la propia
                            tabla. Una vez aprobada, contacta con tu administrador para
                            modificarla.
                        </Callout>
                    </Section>

                    <Section id="projects-request" title="Solicitar asignación a proyectos">
                        <p>
                            Cuando necesites cargar horas en un proyecto al que no estás
                            asignado, usa el botón <b>“Solicitar proyecto”</b> en la
                            página de Parte de horas.
                        </p>
                        <ol className="list-decimal list-inside space-y-1">
                            <li>Filtra por anualidad si el listado es largo.</li>
                            <li>
                                Marca uno o varios proyectos y opcionalmente añade un
                                comentario para el Project Manager.
                            </li>
                            <li>
                                Envía la solicitud. El Project Manager (o un administrador)
                                la revisará y aprobará. Recibirás la asignación de forma
                                automática al ser aprobada.
                            </li>
                        </ol>
                    </Section>

                    <Section id="reports" title="Informes">
                        <p>
                            Explora dos vistas de informes accesibles desde el menú lateral:
                        </p>
                        <ul className="list-disc list-inside space-y-1">
                            <li>
                                <b>Detallado</b>: listado línea a línea de todas las
                                entradas del periodo elegido, con filtros por cliente,
                                proyecto y empleado (si eres admin/PM).
                            </li>
                            <li>
                                <b>Por proyecto y persona</b>: agregado por empleado y
                                proyecto, con horas, tarifa y importe total. Ideal para
                                facturación.
                            </li>
                        </ul>
                        <p>
                            Ambos informes se exportan a <b>CSV</b> (Excel). El detallado
                            además ofrece PDF.
                        </p>
                    </Section>

                    {isPmOrAdmin && (
                        <Section id="pm" title="Rol Project Manager">
                            <p>
                                Como PM tienes acceso al módulo <b>Aprobaciones</b> donde
                                verás las solicitudes de asignación a los proyectos que
                                gestionas.
                            </p>
                            <ul className="list-disc list-inside space-y-1">
                                <li>
                                    Aprobar una solicitud crea la asignación
                                    automáticamente.
                                </li>
                                <li>
                                    Al rechazar puedes añadir un motivo que verá el
                                    solicitante.
                                </li>
                                <li>
                                    Además ves los <b>informes de tus proyectos</b> en
                                    /reports para monitorizar horas y coste.
                                </li>
                            </ul>
                        </Section>
                    )}

                    {isAdmin && (
                        <>
                            <Section id="admin-users" title="Usuarios">
                                <p>
                                    En <b>Administración → Usuarios</b> puedes crear,
                                    editar o dar de baja personas. Cada usuario tiene rol
                                    (Empleado / Project Manager / Administrador), jornada
                                    semanal y días de vacaciones anuales.
                                </p>
                            </Section>

                            <Section id="admin-projects" title="Proyectos y presupuesto">
                                <p>
                                    Al <b>crear o editar un proyecto</b> puedes definir las
                                    horas planificadas <b>por grupo de tarifa</b>. El
                                    sistema calcula automáticamente:
                                </p>
                                <ul className="list-disc list-inside space-y-1">
                                    <li>
                                        <b>Total de horas</b> = suma de horas por grupo.
                                    </li>
                                    <li>
                                        <b>Presupuesto estimado</b> = Σ (horas × tarifa
                                        del grupo).
                                    </li>
                                </ul>
                                <p>
                                    El <b>Manager del proyecto</b> es quien recibe las
                                    solicitudes de asignación de ese proyecto.
                                </p>
                            </Section>

                            <Section id="admin-customers" title="Clientes">
                                <p>
                                    Crea y edita clientes desde <b>Administración →
                                    Clientes</b>. El código de cliente ayuda a que
                                    aparezcan de forma consistente en informes CSV
                                    (columna Cliente).
                                </p>
                            </Section>

                            <Section id="admin-matrix" title="Matriz de vacaciones">
                                <p>
                                    En <b>Administración → Matriz vacaciones</b> ves un
                                    calendario de todo el año con las vacaciones aprobadas
                                    del equipo:
                                </p>
                                <ul className="list-disc list-inside space-y-1">
                                    <li>
                                        <b>Vista anual</b>: 12 meses en bloques de 3, un
                                        cuadrado por día.
                                    </li>
                                    <li>
                                        <b>Vista mensual</b>: un mes completo ampliado.
                                    </li>
                                    <li>
                                        Configura los <b>días de oficina cerrada</b>{" "}
                                        (festivos empresariales, puentes) desde el botón
                                        de la esquina.
                                    </li>
                                </ul>
                            </Section>

                            <Section id="admin-approvals" title="Aprobaciones">
                                <p>
                                    Bandeja unificada de:
                                </p>
                                <ul className="list-disc list-inside space-y-1">
                                    <li>
                                        <b>Ausencias</b>: aprobar/rechazar vacaciones y
                                        ausencias del equipo.
                                    </li>
                                    <li>
                                        <b>Asignaciones</b>: solicitudes de acceso a
                                        proyectos por parte de los empleados.
                                    </li>
                                </ul>
                                <p>
                                    El botón <b>“Vaciar todo el historial”</b> elimina{" "}
                                    <b>permanentemente</b> todas las solicitudes de
                                    asignación (útil para limpieza inicial en producción).
                                </p>
                            </Section>
                        </>
                    )}

                    {isPmOrAdmin && (
                        <Section id="admin-invoices" title="Facturación">
                            <p>
                                En <b>Administración → Facturación</b> registras las
                                facturas emitidas y sigues su estado a lo largo del año.
                            </p>
                            <SubHeading>Campos por factura</SubHeading>
                            <ul className="list-disc list-inside space-y-1">
                                <li>
                                    <b>Tipo</b>: Consultoría (C), Desarrollo (D), Zendesk
                                    (Z), Asistencia (A), Recurrente (R).
                                </li>
                                <li>
                                    <b>Proyecto</b>: al seleccionar uno, se auto-rellena
                                    el PM y el cliente. Puedes cambiarlos manualmente.
                                </li>
                                <li>
                                    <b>Empresa</b>: quien recibe la factura (puede diferir
                                    del cliente final del proyecto).
                                </li>
                                <li>
                                    <b>Estado</b>: Previsto (aún no emitida) → Emitida →
                                    Cobrada. También <b>Anulada</b> para deshacer.
                                </li>
                            </ul>
                            <SubHeading>Panel superior</SubHeading>
                            <p>
                                Muestra el total facturado del año y el desglose por
                                estado. Cambia el año en el filtro para ver otros
                                periodos. Exporta a CSV (UTF-8 con BOM) en cualquier
                                momento con el botón <b>“Exportar CSV”</b>.
                            </p>
                            <Callout icon={Lightbulb} tone="info">
                                Las filas <b>Cobradas</b> se resaltan en verde y las{" "}
                                <b>Anuladas</b> aparecen tachadas.
                            </Callout>
                        </Section>
                    )}

                    {isAdmin && (
                        <></>
                    )}

                    {isAdmin && (
                        <>
                            <Section id="admin-data" title="Importar / exportar datos">
                                <p>
                                    En <b>Administración → Datos</b> puedes:
                                </p>
                                <ul className="list-disc list-inside space-y-1">
                                    <li>
                                        <b>Importar backup XML</b> de eHour (clientes,
                                        proyectos, asignaciones, entradas históricas).
                                    </li>
                                    <li>
                                        <b>Importar CSV</b> con datos generados por la
                                        aplicación.
                                    </li>
                                    <li>
                                        <b>Exportar</b> todo el conjunto a XML o CSV para
                                        respaldo.
                                    </li>
                                </ul>
                                <Callout icon={Warning} tone="warning">
                                    La importación puede reemplazar o mezclar datos
                                    existentes. Haz siempre una exportación previa antes
                                    de importar en producción.
                                </Callout>
                            </Section>
                        </>
                    )}

                    <Section id="faq" title="Preguntas frecuentes">
                        <SubHeading>No veo un proyecto en mi Parte de horas</SubHeading>
                        <p>
                            Puede estar archivado, cerrado (fecha fin pasada) o no tener
                            tu asignación vigente. Solicítalo desde “Solicitar proyecto” o
                            contacta con tu PM.
                        </p>
                        <SubHeading>He añadido horas por error</SubHeading>
                        <p>
                            Puedes editarlas o borrarlas mientras la semana esté abierta.
                            Pulsa sobre la celda y modifica el valor a <code>0</code> o
                            elimina la entrada desde el menú contextual.
                        </p>
                        <SubHeading>¿Cómo se calcula el importe de mis horas?</SubHeading>
                        <p>
                            Tu tarifa es la del <b>grupo</b> al que perteneces. Si tu
                            grupo cambia, todas las horas anteriores conservan su tarifa
                            en el momento del cálculo del informe según el grupo <i>actual</i>{" "}
                            del usuario.
                        </p>
                        <SubHeading>Los caracteres <code>ñ</code> aparecen mal en Excel</SubHeading>
                        <p>
                            Todos los CSV se generan con codificación <b>UTF-8 con BOM</b>{" "}
                            para que Excel los abra correctamente. Si aún así lo ves mal,
                            abre Excel primero y usa <b>Datos → Desde texto/CSV</b>
                            seleccionando UTF-8.
                        </p>
                    </Section>

                    <div className="text-xs text-slate-400 text-center pt-4 border-t border-slate-200">
                        Versión del manual · Julio 2026 · Ingartek Portal
                    </div>
                </div>
            </div>
        </div>
    );
}

function Section({ id, title, children }) {
    return (
        <section
            id={`manual-${id}`}
            className="scroll-mt-6"
            data-testid={`manual-section-${id}`}
        >
            <h2 className="font-display text-2xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-200">
                {title}
            </h2>
            <div className="prose prose-sm max-w-none text-slate-700 space-y-3 [&_code]:bg-slate-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded-sm [&_code]:text-[12px] [&_code]:text-slate-800 [&_code]:font-mono">
                {children}
            </div>
        </section>
    );
}

function SubHeading({ children }) {
    return (
        <h3 className="font-display text-base font-semibold text-slate-800 mt-4 mb-1">
            {children}
        </h3>
    );
}

function Callout({ icon: Icon, tone = "info", children }) {
    const styles = {
        info: "bg-sky-50 border-sky-200 text-sky-900",
        warning: "bg-amber-50 border-amber-200 text-amber-900",
        danger: "bg-rose-50 border-rose-200 text-rose-900",
    };
    return (
        <div
            className={`border-l-2 px-4 py-3 text-sm flex gap-2 ${styles[tone]}`}
        >
            <Icon size={16} className="flex-shrink-0 mt-0.5" />
            <div>{children}</div>
        </div>
    );
}
